import { trpc } from "@/lib/trpc";
import { clearSessionToken, getSessionToken } from "@/_core/session";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getApiBaseUrl, getLoginUrl } from "./const";
import "./index.css";

const queryClient = new QueryClient();
const API_REQUEST_TIMEOUT_MS = 300_000;
const ARTICLE_GENERATION_TIMEOUT_MS = 900_000;

const getRequestTimeoutMs = (input: RequestInfo | URL): number => {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  return url.includes("articleWorkbench.prepareOutput")
    ? ARTICLE_GENERATION_TIMEOUT_MS
    : API_REQUEST_TIMEOUT_MS;
};

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized =
    error.message === UNAUTHED_ERR_MSG ||
    error.data?.code === "UNAUTHORIZED" ||
    error.data?.httpStatus === 401;

  if (!isUnauthorized) return;

  // Prevent "flash loop": clear token before redirect so we don't keep sending a bad session.
  clearSessionToken();

  // If OAuth redirected the user to an "unauthorized" page, keep them there.
  if (window.location.pathname === "/unauthorized") return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${getApiBaseUrl()}/api/trpc`,
      transformer: superjson,
      fetch(input, init) {
        // Hard timeout to prevent "stuck forever" UI in long-running queries/mutations.
        // Always enforce our timeout even if the caller provides a signal.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), getRequestTimeoutMs(input));

        const upstreamSignal = init?.signal;
        const onUpstreamAbort = () => controller.abort();
        if (upstreamSignal) {
          if (upstreamSignal.aborted) controller.abort();
          else upstreamSignal.addEventListener("abort", onUpstreamAbort, { once: true });
        }

        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
          headers: {
            ...(init?.headers ?? {}),
            ...(getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {}),
          },
          signal: controller.signal,
        }).finally(() => {
          clearTimeout(timeout);
          if (upstreamSignal) upstreamSignal.removeEventListener("abort", onUpstreamAbort);
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
